import { useState } from 'react';
import { DollarSign, AlertCircle, User, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRegisterSession, Register } from '@/hooks/useRegisterSession';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';

interface OpenRegisterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function OpenRegisterModal({ open, onOpenChange }: OpenRegisterModalProps) {
  const [selectedRegisterId, setSelectedRegisterId] = useState<string>('');
  const [openingBalance, setOpeningBalance] = useState<string>('');
  const { availableRegisters, registersInUse, openRegister, registersLoading } =
    useRegisterSession();

  // Use $ as default currency symbol
  const currencySymbol = '$';

  const handleOpenRegister = async () => {
    if (!selectedRegisterId) {
      toast.error('Select a register', {
        description: 'Please select a register to open.',
      });
      return;
    }

    const balance = parseFloat(openingBalance) || 0;
    if (balance < 0) {
      toast.error('Invalid balance', {
        description: 'Opening balance cannot be negative.',
      });
      return;
    }

    try {
      await openRegister.mutateAsync({
        registerId: selectedRegisterId,
        openingBalance: balance,
      });

      toast.success('Register opened', {
        description: 'Your register session has been started.',
      });

      // Reset form and close modal
      setSelectedRegisterId('');
      setOpeningBalance('');
      onOpenChange(false);
    } catch (error: any) {
      toast.error('Failed to open register', {
        description: error.message || 'An error occurred while opening the register.',
      });
    }
  };

  const handleBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimal point
    const value = e.target.value.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) return;
    if (parts[1]?.length > 2) return;
    setOpeningBalance(value);
  };

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent maxWidth="md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>Open Register</ResponsiveModalTitle>
          <ResponsiveModalDescription>
            Select a register and enter your opening cash balance to start your shift.
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-6">
          {/* Register Selection */}
          <div className="space-y-2">
            <Label htmlFor="register">Select Register</Label>
            {registersLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : availableRegisters.length === 0 ? (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  No registers available. All registers are currently in use.
                </p>
              </div>
            ) : (
              <Select value={selectedRegisterId} onValueChange={setSelectedRegisterId}>
                <SelectTrigger id="register">
                  <SelectValue placeholder="Choose a register..." />
                </SelectTrigger>
                <SelectContent>
                  {availableRegisters.map((register: Register) => (
                    <SelectItem key={register.id} value={register.id}>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span>{register.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Registers in use info */}
          {registersInUse.length > 0 && (
            <div className="space-y-2">
              <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                Currently in use
              </Label>
              <div className="space-y-1">
                {registersInUse.map((register: Register) => (
                  <div
                    key={register.id}
                    className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{register.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {register.current_user_name}
                      </span>
                      <Badge variant="secondary" className="text-xs">
                        In use
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Opening Balance */}
          <div className="space-y-2">
            <Label htmlFor="opening-balance">Opening Cash Balance</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {currencySymbol}
              </span>
              <Input
                id="opening-balance"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={openingBalance}
                onChange={handleBalanceChange}
                className="pl-8"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Enter the amount of cash in the drawer when starting your shift.
            </p>
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleOpenRegister}
            disabled={
              !selectedRegisterId || openRegister.isPending || availableRegisters.length === 0
            }
          >
            {openRegister.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Opening...
              </>
            ) : (
              <>
                <DollarSign className="mr-2 h-4 w-4" />
                Open Register
              </>
            )}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
